import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Form, Button, Card, Row, Col, Alert, Badge, Accordion } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import PageHeader from '../components/PageHeader.jsx';

// ---------- Per-feature value editor (small, focused) ----------
function FeatureValueEditor({ feature, value, onChange }) {
  const v = value ?? feature.default_value;

  switch (feature.feature_type) {
    case 'boolean':
      // Boolean features only have an enable toggle — that's the row toggle, no extra editor needed.
      return null;

    case 'quota':
      return (
        <div className="d-flex align-items-center gap-2">
          <Form.Label className="mb-0 small text-muted">Limit</Form.Label>
          <Form.Control type="number" min="0" size="sm" style={{ width: 110 }}
            value={v.limit ?? 0}
            onChange={(e) => onChange({ ...v, limit: parseInt(e.target.value, 10) || 0 })} />
        </div>
      );

    case 'scope':
      return (
        <Form.Control as="textarea" rows={2} size="sm" placeholder="ein Eintrag pro Zeile"
          value={(v.allowed || []).join('\n')}
          onChange={(e) => onChange({ ...v, allowed: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })} />
      );

    case 'rate_limit':
      return (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Form.Label className="mb-0 small text-muted">Anfragen / Zeit</Form.Label>
          <Form.Control type="number" min="1" size="sm" style={{ width: 90 }}
            value={v.count ?? 1}
            onChange={(e) => onChange({ ...v, count: parseInt(e.target.value, 10) || 1 })} />
          <Form.Select size="sm" style={{ width: 100 }} value={v.per_unit ?? 'min'}
            onChange={(e) => onChange({ ...v, per_unit: e.target.value })}>
            <option value="sec">Sek.</option>
            <option value="min">Min.</option>
            <option value="hour">Std.</option>
            <option value="day">Tag</option>
          </Form.Select>
        </div>
      );

    case 'time_window':
      return (
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Form.Label className="mb-0 small text-muted">Von</Form.Label>
          <Form.Control type="datetime-local" size="sm" style={{ width: 220 }}
            value={v.starts_at ? v.starts_at.slice(0, 16) : ''}
            onChange={(e) => onChange({ ...v, starts_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
          <Form.Label className="mb-0 small text-muted">bis</Form.Label>
          <Form.Control type="datetime-local" size="sm" style={{ width: 220 }}
            value={v.ends_at ? v.ends_at.slice(0, 16) : ''}
            onChange={(e) => onChange({ ...v, ends_at: e.target.value ? new Date(e.target.value).toISOString() : null })} />
        </div>
      );

    default:
      return (
        <Form.Control as="textarea" rows={3} className="font-monospace small"
          value={JSON.stringify(v, null, 2)}
          onChange={(e) => { try { onChange(JSON.parse(e.target.value)); } catch { /* ignore */ } }} />
      );
  }
}

// ---------- Feature row ----------
function FeatureRow({ feature, isBound, value, onToggle, onValueChange }) {
  return (
    <div className={`feature-row ${isBound ? 'is-bound' : ''}`}>
      <div className="d-flex align-items-center gap-3">
        <Form.Check type="switch" id={`feat-${feature.id}`}
          checked={isBound} onChange={onToggle} className="mb-0" />
        <div className="flex-grow-1">
          <label htmlFor={`feat-${feature.id}`} className="d-flex align-items-center gap-2 mb-0" style={{ cursor: 'pointer' }}>
            <span className="fw-semibold">{feature.name}</span>
            <code className="small text-muted">{feature.key}</code>
          </label>
          {feature.description && <div className="text-muted small mt-1">{feature.description}</div>}
        </div>
        {isBound && feature.feature_type !== 'boolean' && (
          <div className="feature-row-editor">
            <FeatureValueEditor feature={feature} value={value} onChange={onValueChange} />
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Main page ----------
export default function NewLicensePage() {
  const navigate = useNavigate();

  const customers = useQuery({
    queryKey: ['customers-all'],
    queryFn: () => api.get('/customers', { params: { limit: 1000 } }).then(r => r.data.items)
  });
  const features = useQuery({
    queryKey: ['features'],
    queryFn: () => api.get('/features').then(r => r.data.items)
  });

  const [customerId, setCustomerId] = useState('');
  const [name, setName] = useState('');
  const [planLabel, setPlanLabel] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [note, setNote] = useState('');
  const [bound, setBound] = useState({});      // { feature_id: { feature, value } }
  const [error, setError] = useState(null);
  const [issued, setIssued] = useState(null);

  // Group features by category for cleaner layout
  const grouped = useMemo(() => {
    const map = {};
    for (const f of (features.data || [])) {
      const cat = f.category || 'Sonstige';
      (map[cat] ||= []).push(f);
    }
    return map;
  }, [features.data]);

  const issue = useMutation({
    mutationFn: (body) => api.post('/licenses', body).then(r => r.data),
    onSuccess: (data) => setIssued(data),
    onError: (err) => setError(err.response?.data?.message || JSON.stringify(err.response?.data) || err.message)
  });

  const toggleFeature = (f) => {
    setBound(prev => {
      const copy = { ...prev };
      if (copy[f.id]) delete copy[f.id];
      else copy[f.id] = { feature: f, value: { ...f.default_value, enabled: true } };
      return copy;
    });
  };

  const setFeatureValue = (f, value) => {
    setBound(prev => ({ ...prev, [f.id]: { feature: f, value } }));
  };

  const submit = (e) => {
    e.preventDefault();
    setError(null);
    const body = {
      customer_id: customerId,
      name,
      plan_label: planLabel || null,
      valid_until: validUntil ? new Date(validUntil).toISOString() : null,
      note: note || null,
      features: Object.fromEntries(Object.entries(bound).map(([_id, item]) => [item.feature.key, item.value]))
    };
    issue.mutate(body);
  };

  const selectedCount = Object.keys(bound).length;
  const canSubmit = customerId && name && !issue.isLoading;

  // ---- Result page after issue ----
  if (issued) {
    return (
      <>
        <PageHeader title="Lizenz ausgestellt"
          subtitle={<>Für <strong>{customers.data?.find(c => c.id === issued.license.customer_id)?.name}</strong></>} />
        <Alert variant="success" className="d-flex gap-3 align-items-center">
          <Icon icon="tabler:circle-check" style={{ fontSize: 28 }} />
          <div>
            <div className="fw-semibold">{issued.license.name}</div>
            <div className="small">jti: <code>{issued.license.jti}</code></div>
          </div>
        </Alert>
        <Card className="card-flat">
          <Card.Body>
            <h5 className="mb-2">Token</h5>
            <p className="text-muted small mb-3">Jetzt kopieren — wird nicht erneut angezeigt.</p>
            <textarea className="form-control font-monospace small"
              rows={6} value={issued.token} readOnly onClick={(e) => e.target.select()} />
            <div className="d-flex gap-2 mt-3">
              <Button onClick={() => navigator.clipboard.writeText(issued.token)}>
                <Icon icon="tabler:copy" className="me-1" />Token kopieren
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate(`/licenses/${issued.license.id}`)}>
                Zur Lizenz-Detailseite
              </Button>
              <Button variant="outline-secondary" onClick={() => navigate('/licenses')}>
                Zurück zur Liste
              </Button>
            </div>
          </Card.Body>
        </Card>
      </>
    );
  }

  // ---- Form ----
  return (
    <Form onSubmit={submit}>
      <PageHeader
        title="Neue Lizenz"
        subtitle="Stelle eine Lizenz für einen Kunden aus, mit den gewünschten Features."
        actions={
          <>
            <Button variant="outline-secondary" type="button" onClick={() => navigate('/licenses')}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {issue.isLoading
                ? <><span className="spinner-border spinner-border-sm me-2" />Stelle aus…</>
                : <><Icon icon="tabler:certificate" className="me-1" />Lizenz ausstellen</>}
            </Button>
          </>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          <pre className="mb-0 small">{error}</pre>
        </Alert>
      )}

      <Row className="g-3">
        {/* ---- Stammdaten ---- */}
        <Col lg={5}>
          <Card className="card-flat mb-3">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>1. Stammdaten</h6>
              <Form.Group className="mb-3">
                <Form.Label>Kunde *</Form.Label>
                <Form.Select required value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
                  <option value="">— wählen —</option>
                  {(customers.data || []).map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.external_id ? ` (${c.external_id})` : ''}
                    </option>
                  ))}
                </Form.Select>
                {(customers.data || []).length === 0 && (
                  <Form.Text>Keine Kunden vorhanden — <a href="/customers">erst einen anlegen</a>.</Form.Text>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Bezeichnung *</Form.Label>
                <Form.Control required value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="z.B. Production 2026" />
                <Form.Text>Hilft beim Auseinanderhalten mehrerer Lizenzen pro Kunde.</Form.Text>
              </Form.Group>

              <Row className="g-2 mb-3">
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Plan</Form.Label>
                    <Form.Control value={planLabel} onChange={(e) => setPlanLabel(e.target.value)}
                      placeholder="z.B. Pro" />
                  </Form.Group>
                </Col>
                <Col sm={6}>
                  <Form.Group>
                    <Form.Label>Gültig bis</Form.Label>
                    <Form.Control type="datetime-local"
                      value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group>
                <Form.Label>Notiz <span className="text-muted small">(intern)</span></Form.Label>
                <Form.Control as="textarea" rows={2}
                  value={note} onChange={(e) => setNote(e.target.value)} />
              </Form.Group>
            </Card.Body>
          </Card>

          {/* Summary card */}
          <Card className="card-flat">
            <Card.Body>
              <h6 className="text-uppercase text-muted small mb-3" style={{ letterSpacing: '0.05em' }}>Zusammenfassung</h6>
              <div className="d-flex justify-content-between mb-2">
                <span className="text-muted">Features ausgewählt</span>
                <span className="fw-semibold">{selectedCount}</span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="text-muted">Status</span>
                {canSubmit ? <Badge bg="" className="badge-soft-success">Bereit</Badge>
                           : <Badge bg="" className="badge-soft-warning">Stammdaten unvollständig</Badge>}
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* ---- Features ---- */}
        <Col lg={7}>
          <Card className="card-flat">
            <Card.Body className="pb-1">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="text-uppercase text-muted small mb-1" style={{ letterSpacing: '0.05em' }}>2. Features</h6>
                  <div className="text-muted small">{selectedCount} von {features.data?.length || 0} aktiviert</div>
                </div>
                {selectedCount > 0 && (
                  <Button size="sm" variant="outline-secondary" type="button" onClick={() => setBound({})}>
                    Alles abwählen
                  </Button>
                )}
              </div>
            </Card.Body>

            <Accordion alwaysOpen defaultActiveKey={Object.keys(grouped)} flush>
              {Object.entries(grouped).map(([category, feats]) => {
                const inThisCat = feats.filter(f => bound[f.id]).length;
                return (
                  <Accordion.Item eventKey={category} key={category}>
                    <Accordion.Header>
                      <span className="fw-semibold text-capitalize">{category}</span>
                      <Badge bg="" className={`ms-2 ${inThisCat > 0 ? 'badge-soft-primary' : 'badge-soft-secondary'}`}>
                        {inThisCat}/{feats.length}
                      </Badge>
                    </Accordion.Header>
                    <Accordion.Body className="p-0">
                      {feats.map(f => (
                        <FeatureRow key={f.id} feature={f}
                          isBound={!!bound[f.id]}
                          value={bound[f.id]?.value}
                          onToggle={() => toggleFeature(f)}
                          onValueChange={(v) => setFeatureValue(f, v)} />
                      ))}
                    </Accordion.Body>
                  </Accordion.Item>
                );
              })}
            </Accordion>
          </Card>
        </Col>
      </Row>
    </Form>
  );
}
